/**
 * 积分扣费规则：拉取 / 缓存 / 文案
 * 对接 POST /member/points/deduction_rules（points-deduction-rules-api）
 */
import { pointsDeductionRules } from '@/api/englishLearning/public'

/** 场景标识（与后端 scene 一致） */
export const POINTS_SCENE = {
  SPOKEN_EVALUATION: 'spoken_evaluation',
  AI_COURSE: 'ai_course',
  SPOKEN_SQUARE: 'spoken_square',
  IMAGE_RECOGNITION_WORD: 'image_recognition_word',
  IMAGE_RECOGNITION_SENTENCE: 'image_recognition_sentence',
  IMAGE_RECOGNITION: 'image_recognition'
}

/** @type {Record<string, object>} */
let rulesByScene = {}
let loadPromise = null
let loaded = false

function toAmount (value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return null
  return Number(n.toFixed(2))
}

function formatAmount (value) {
  const n = toAmount(value)
  if (n === null) return ''
  return String(n)
}

function normalizeList (data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.list)) return data.list
  if (data && typeof data === 'object') {
    return Object.keys(data).map((scene) => ({
      scene,
      ...(data[scene] && typeof data[scene] === 'object' ? data[scene] : {})
    }))
  }
  return []
}

function applyRules (list) {
  const map = {}
  list.forEach((item) => {
    const scene = String(item?.scene || '').trim()
    if (!scene) return
    map[scene] = item
  })
  rulesByScene = map
  loaded = true
}

/**
 * 拉取并缓存扣费规则（并发去重）
 * @returns {Promise<Record<string, object>>}
 */
export async function ensureDeductionRules () {
  if (loaded) return rulesByScene
  if (loadPromise) return loadPromise

  loadPromise = (async () => {
    try {
      const res = await pointsDeductionRules({})
      if (Number(res?.code) === 1 || Number(res?.code) === 200) {
        applyRules(normalizeList(res?.data))
      } else {
        rulesByScene = {}
        loaded = false
      }
    } catch (e) {
      console.warn('[pointsDeductionRules] 拉取失败', e)
      rulesByScene = {}
      loaded = false
    } finally {
      loadPromise = null
    }
    return rulesByScene
  })()

  return loadPromise
}

/** 启动预取（不阻塞、失败静默） */
export function initDeductionRules () {
  return ensureDeductionRules().catch(() => ({}))
}

/**
 * 生成展示文案（对齐文档 §6）
 * - 固定/按条场景：直白「消耗 / 每条消息」等
 * - image_recognition：按张预扣，随上传数量同步；实扣按单词/句子结算（多退少补）
 * - 场景未开启 / 无数据：返回空串（模板用 v-if 隐藏）
 *
 * @param {string} scene POINTS_SCENE.*
 * @param {{ unitLabel?: string, imageCount?: number }} [options]
 */
export function formatDeductionTip (scene, options = {}) {
  const rule = rulesByScene[scene]
  if (!rule) return ''

  const points = formatAmount(rule.points)
  const perImage = formatAmount(rule.reserve_per_image)
  const reserve = formatAmount(rule.reserve_points)

  if (scene === POINTS_SCENE.IMAGE_RECOGNITION) {
    if (!perImage) return ''
    const count = Math.max(0, Number(options.imageCount) || 0)
    // 明示：扣费发生在识别，与是否新建章节/课程无关
    const settleNote = '识别成功即扣积分，即使不新建章节也会扣费'
    if (count <= 0) {
      return `一张图片预扣 ${perImage} 积分（多退少补）；${settleNote}`
    }
    const total = formatAmount(Number(rule.reserve_per_image) * count)
    return `一张图片预扣 ${perImage} 积分，已选 ${count} 张共预扣 ${total} 积分（多退少补）；${settleNote}`
  }

  // 线上 spoken_square 可能仅返回 reserve_points（无 points）；有 points 时按文档展示单价
  if (scene === POINTS_SCENE.SPOKEN_SQUARE) {
    if (points && reserve) return `每条消息 ${points} 积分，操作前预扣 ${reserve} 积分`
    if (points) return `每条消息 ${points} 积分`
    if (reserve) return `每条消息预扣 ${reserve} 积分`
    return ''
  }

  if (!points) return ''

  switch (scene) {
    case POINTS_SCENE.SPOKEN_EVALUATION:
      return `每次测评消耗 ${points} 积分`
    case POINTS_SCENE.AI_COURSE: {
      const unit = String(options.unitLabel || '每题').trim() || '每题'
      return `${unit}消耗 ${points} 积分`
    }
    case POINTS_SCENE.IMAGE_RECOGNITION_WORD:
      return `每个单词 ${points} 积分`
    case POINTS_SCENE.IMAGE_RECOGNITION_SENTENCE:
      return `每个句子 ${points} 积分`
    default:
      return `${points} 积分`
  }
}

export function getDeductionRule (scene) {
  return rulesByScene[scene] || null
}

function pickAmount (obj, keys = []) {
  if (!obj || typeof obj !== 'object') return null
  for (const key of keys) {
    if (obj[key] === undefined || obj[key] === null || obj[key] === '') continue
    const n = toAmount(obj[key])
    if (n !== null) return n
  }
  return null
}

/**
 * 识别结算详情（优先 billing.total_points；预扣按张数 × reserve_per_image）
 * billing 示例字段：status / model_points / asr_points / total_points /
 * estimated_points / balance / required_points / shortage_points
 *
 * @returns {{
 *   tip: string,
 *   actual: number|null,
 *   prepaid: number|null,
 *   refund: number|null,
 *   supplement: number|null,
 *   balance: number|null,
 *   actualText: string,
 *   prepaidText: string,
 *   refundText: string,
 *   supplementText: string,
 *   balanceText: string,
 *   wordCount: number,
 *   sentenceCount: number,
 *   status: string
 * }}
 */
export function buildImageSettlementInfo (payload = {}) {
  const empty = {
    tip: '',
    actual: null,
    prepaid: null,
    refund: null,
    supplement: null,
    balance: null,
    actualText: '',
    prepaidText: '',
    refundText: '',
    supplementText: '',
    balanceText: '',
    wordCount: 0,
    sentenceCount: 0,
    status: ''
  }

  const billing = payload.billing && typeof payload.billing === 'object'
    ? payload.billing
    : null

  const imageRule = rulesByScene[POINTS_SCENE.IMAGE_RECOGNITION] || {}
  const wordRule = rulesByScene[POINTS_SCENE.IMAGE_RECOGNITION_WORD] || {}
  const sentenceRule = rulesByScene[POINTS_SCENE.IMAGE_RECOGNITION_SENTENCE] || {}

  const imageCount = Math.max(0, Number(payload.imageCount) || 0)
  const wordCount = Math.max(0, Number(payload.wordCount) || 0)
  const sentenceCount = Math.max(0, Number(payload.sentenceCount) || 0)

  const perImage = toAmount(imageRule.reserve_per_image)
  const wordUnit = toAmount(wordRule.points)
  const sentenceUnit = toAmount(sentenceRule.points)

  const prepaidFallback =
    perImage !== null && imageCount > 0 ? Number((perImage * imageCount).toFixed(2)) : null
  const actualFallback = Number((
    (wordUnit !== null ? wordUnit * wordCount : 0) +
    (sentenceUnit !== null ? sentenceUnit * sentenceCount : 0)
  ).toFixed(2))

  let actual = pickAmount(billing, [
    'total_points',
    'required_points',
    'estimated_points',
    'charge_points',
    'settled_points',
    'points',
    'actual_points'
  ])
  let prepaid = pickAmount(billing, [
    'reserve_points',
    'reserved_points',
    'pre_points',
    'prepaid_points',
    'freeze_points'
  ])

  const refundFromApi = pickAmount(billing, [
    'refund_points',
    'return_points',
    'refunded_points'
  ])
  const supplementFromApi = pickAmount(billing, [
    'supplement_points',
    'extra_points',
    'makeup_points',
    'additional_points'
  ])
  const shortage = pickAmount(billing, ['shortage_points'])
  const balance = pickAmount(billing, ['balance', 'points_balance', 'points_total'])

  if (actual === null) {
    if (!billing && !wordCount && !sentenceCount && actualFallback === 0) return empty
    actual = actualFallback
  }
  if (prepaid === null) prepaid = prepaidFallback

  if (actual === null) return empty

  let refund = null
  let supplement = null
  if (refundFromApi !== null && refundFromApi > 0) {
    refund = refundFromApi
  } else if (supplementFromApi !== null && supplementFromApi > 0) {
    supplement = supplementFromApi
  } else if (shortage !== null && shortage > 0) {
    supplement = shortage
  } else if (prepaid !== null) {
    const diff = Number((prepaid - actual).toFixed(2))
    if (diff > 0) refund = diff
    else if (diff < 0) supplement = Math.abs(diff)
  }

  const actualText = formatAmount(actual)
  const prepaidText = prepaid !== null ? formatAmount(prepaid) : ''
  const refundText = refund !== null ? formatAmount(refund) : ''
  const supplementText = supplement !== null ? formatAmount(supplement) : ''
  const balanceText = balance !== null ? formatAmount(balance) : ''

  let tip = `实扣 ${actualText} 积分`
  if (refundText) tip += `，已返还 ${refundText} 积分`
  else if (supplementText) tip += `，已补扣 ${supplementText} 积分`

  return {
    tip,
    actual,
    prepaid,
    refund,
    supplement,
    balance,
    actualText,
    prepaidText,
    refundText,
    supplementText,
    balanceText,
    wordCount,
    sentenceCount,
    status: String(billing?.status || '')
  }
}

/** 识别结算短文案：实扣 + 已返还 / 已补扣 */
export function formatImageSettlementTip (payload = {}) {
  return buildImageSettlementInfo(payload).tip
}

export default {
  POINTS_SCENE,
  ensureDeductionRules,
  initDeductionRules,
  formatDeductionTip,
  formatImageSettlementTip,
  buildImageSettlementInfo,
  getDeductionRule
}
